import { useContext, useEffect, useRef, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { safeGetAccessToken } from "../../utils/auth";
import { copyToClipboard } from "../../utils/clipboard";
import { QRCodeSVG } from "qrcode.react";
import { Check, CheckCircle2, Copy, Loader2 } from "lucide-react";
import { PaymentContext } from "../../contexts/PaymentContext";
import { API_BASE } from "../../shared/Constants";
import { Button } from "../ui/button";
import { Card } from "../ui/card";
import { reportHandledError, reportApiError } from "../../utils/reportError";

interface PixData {
  tx_id: string;
  emv: string;
  expires_at: string;
  amount: number;
}

type PixState = "input" | "loading" | "pending" | "paid" | "expired" | "error";

export default function PixCheckout() {
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();
  const { setPaymentStage, setPaymentToast, billingScope, activeOrganizationId } =
    useContext(PaymentContext);

  const [amount, setAmount] = useState<string>("");
  const [pixData, setPixData] = useState<PixData | null>(null);
  const [pixState, setPixState] = useState<PixState>("input");
  const [copied, setCopied] = useState(false);
  const [timeLeft, setTimeLeft] = useState<string>("");
  const [inputMessage, setInputMessage] = useState("");
  const pollIntervalRef = useRef<NodeJS.Timer | null>(null);
  const countdownRef = useRef<NodeJS.Timer | null>(null);

  const buildPixUrl = (path: string, params: Record<string, string> = {}) => {
    const search = new URLSearchParams(params);
    if (billingScope === "organization" && activeOrganizationId) {
      search.set("organization_id", activeOrganizationId);
    }
    const query = search.toString();
    return `${API_BASE}/api/billing/pix${path}${query ? `?${query}` : ""}`;
  };

  const formatValue = (inputValue: string) => {
    inputValue = inputValue.replace(/\D/g, "");
    if (inputValue.length > 8) inputValue = inputValue.slice(0, 8);
    while (inputValue.length < 3) inputValue = "0" + inputValue;
    const reals = inputValue.slice(0, -2);
    const cents = inputValue.slice(-2);
    return `${parseInt(reals, 10)}.${cents}`;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formattedValue = formatValue(e.target.value);
    if (parseFloat(formattedValue) > 30000) {
      setInputMessage("O valor máximo é R$ 30.000,00");
      setAmount("30000.00");
    } else {
      setInputMessage("");
      setAmount(formattedValue);
    }
  };

  const formatDisplayAmount = (value: string) => {
    const num = parseFloat(value);
    if (isNaN(num)) return "R$ 0,00";
    return `R$ ${num.toFixed(2).replace(".", ",")}`;
  };

  const updateCountdown = (expiresAt: string) => {
    const now = new Date().getTime();
    const expiry = new Date(expiresAt + "Z").getTime();
    const diff = expiry - now;

    if (diff <= 0) {
      setTimeLeft("Expirado");
      setPixState("expired");
      return;
    }

    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);

    if (hours > 0) setTimeLeft(`${hours}h ${minutes}m ${seconds}s`);
    else if (minutes > 0) setTimeLeft(`${minutes}m ${seconds}s`);
    else setTimeLeft(`${seconds}s`);
  };

  const handleCreateCharge = async () => {
    const numAmount = parseFloat(amount);
    if (isNaN(numAmount) || numAmount < 20) {
      setInputMessage("O valor mínimo é R$ 20,00");
      return;
    }
    setPixState("loading");
    try {
      const url = buildPixUrl("/create-charge", { amount: numAmount.toString() });
      const accessToken = await safeGetAccessToken({ getAccessTokenSilently, loginWithRedirect });
      if (!accessToken) return;
      const response = await fetch(url, {
        method: "POST",
        headers: { Authorization: `Auth ${accessToken}` },
      });
      if (!response.ok) {
        reportApiError(response, { feature: "pix.create", method: "POST" });
        const error = await response.json().catch(() => ({}));
        throw new Error(error.detail || "Erro ao criar cobrança PIX");
      }
      const data: PixData = await response.json();
      setPixData(data);
      setPixState("pending");
    } catch (error: any) {
      reportHandledError(error, { category: "api", feature: "pix.create" });
      setPixState("error");
      setPaymentToast({ type: "error", message: error.message || "Erro ao criar cobrança PIX" });
    }
  };

  useEffect(() => {
    if (pixState !== "pending" || !pixData) return;
    const pollStatus = async () => {
      try {
        const params: Record<string, string> = {};
        if (billingScope === "organization" && activeOrganizationId) {
          params.organization_id = activeOrganizationId;
        }
        const url = buildPixUrl(`/status/${pixData.tx_id}`, params);
        const accessToken = await safeGetAccessToken({ getAccessTokenSilently, loginWithRedirect });
        if (!accessToken) return;
        const response = await fetch(url, {
          method: "GET",
          headers: { Authorization: `Auth ${accessToken}` },
        });
        if (response.ok) {
          const data = await response.json();
          if (data.status === "paid") {
            setPixState("paid");
            setPaymentToast({
              type: "success",
              message: "Pagamento PIX confirmado! Seus créditos foram adicionados.",
            });
          } else if (data.status === "expired") {
            setPixState("expired");
          }
        } else {
          reportApiError(response, { feature: "pix.status", method: "GET" });
        }
      } catch (error) {
        reportHandledError(error, { category: "api", feature: "pix.status", level: "warning" });
      }
    };
    pollStatus();
    pollIntervalRef.current = setInterval(pollStatus, 5000);
    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pixState, pixData]);

  useEffect(() => {
    if (pixState !== "pending" || !pixData) return;
    updateCountdown(pixData.expires_at);
    countdownRef.current = setInterval(() => updateCountdown(pixData.expires_at), 1000);
    return () => {
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, [pixState, pixData]);

  useEffect(() => {
    if (pixState === "paid") {
      const timeout = setTimeout(() => setPaymentStage("refresh"), 3000);
      return () => clearTimeout(timeout);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pixState]);

  const handleCopy = async () => {
    if (pixData?.emv) {
      const ok = await copyToClipboard(pixData.emv);
      if (ok) {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    }
  };

  const handleNewCharge = () => {
    setPixData(null);
    setPixState("input");
    setCopied(false);
    setTimeLeft("");
  };

  if (pixState === "input" || pixState === "loading" || pixState === "error") {
    return (
      <div className="flex flex-col gap-4 p-6">
        <p className="text-[13px] text-muted-foreground">
          Insira o valor desejado para gerar um QR Code PIX. O pagamento é confirmado
          instantaneamente.
        </p>
        <div className="flex flex-col gap-1.5">
          <label className="text-[13px] font-semibold text-foreground">Valor da recarga</label>
          <div className="flex items-center overflow-hidden rounded-md border border-border focus-within:border-ring focus-within:ring-1 focus-within:ring-ring">
            <span className="border-r border-border bg-muted px-3 py-2.5 text-sm font-semibold text-muted-foreground">
              R$
            </span>
            <input
              type="text"
              inputMode="numeric"
              placeholder="0,00"
              value={amount}
              onChange={handleInputChange}
              disabled={pixState === "loading"}
              className="flex-1 bg-background px-3 py-2.5 text-base font-semibold text-foreground outline-none placeholder:font-normal placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-60"
            />
          </div>
          {inputMessage && (
            <p className="text-[12px] font-medium text-destructive">{inputMessage}</p>
          )}
        </div>
        <Button
          onClick={handleCreateCharge}
          disabled={pixState === "loading" || !amount || parseFloat(amount) < 20}
          className="w-full gap-2"
        >
          {pixState === "loading" ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Gerando QR Code...
            </>
          ) : (
            "Gerar QR Code PIX"
          )}
        </Button>
      </div>
    );
  }

  if (pixState === "paid") {
    return (
      <div className="flex flex-col items-center justify-center gap-3 px-6 py-10 text-center">
        <CheckCircle2 className="h-16 w-16 text-success" />
        <h3 className="text-xl font-semibold tracking-tight text-success">
          Pagamento confirmado!
        </h3>
        <p className="text-sm text-muted-foreground">
          Seus créditos de {formatDisplayAmount(pixData?.amount?.toString() || "0")} foram
          adicionados.
        </p>
      </div>
    );
  }

  if (pixState === "expired") {
    return (
      <div className="flex flex-col items-center gap-4 p-6 text-center">
        <h3 className="text-lg font-semibold tracking-tight text-destructive">QR Code expirado</h3>
        <p className="text-sm text-muted-foreground">
          O tempo para pagamento expirou. Gere um novo QR Code para continuar.
        </p>
        <Button onClick={handleNewCharge}>Gerar novo QR Code</Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5 p-6">
      <div className="flex flex-col items-center gap-3">
        <span className="rounded-full border border-success/30 bg-success/10 px-4 py-1 text-base font-semibold text-success">
          {formatDisplayAmount(pixData?.amount?.toString() || "0")}
        </span>
        <Card className="p-4">
          {pixData?.emv && <QRCodeSVG value={pixData.emv} size={220} level="M" includeMargin />}
        </Card>
        <span className="text-[13px] font-medium text-muted-foreground">Expira em: {timeLeft}</span>
      </div>

      <div className="flex flex-col gap-1.5">
        <label className="text-[13px] font-semibold text-foreground">PIX Copia e Cola</label>
        <div className="flex gap-2">
          <input
            readOnly
            value={pixData?.emv || ""}
            onClick={handleCopy}
            className="flex-1 cursor-pointer overflow-hidden text-ellipsis rounded-md border border-border bg-muted px-3 py-2 font-mono text-[12px] text-foreground/80 outline-none hover:border-foreground/30"
          />
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={handleCopy}
            title="Copiar código PIX"
          >
            {copied ? <Check className="h-4 w-4 text-success" /> : <Copy className="h-4 w-4" />}
          </Button>
        </div>
        {copied && <span className="text-[12px] font-medium text-success">Copiado!</span>}
      </div>

      <div className="space-y-1 rounded-md border border-border bg-muted/30 p-4 text-[13px] text-muted-foreground">
        <p>1. Abra o app do seu banco</p>
        <p>2. Escaneie o QR Code ou cole o código PIX</p>
        <p>3. Confirme o pagamento</p>
        <p>4. Seus créditos serão adicionados automaticamente</p>
      </div>
    </div>
  );
}
